// @ts-nocheck
/**
 * ZK Proof Generation Utilities
 *
 * Adapted from the Voidify protocol's client-side proof generation.
 * Uses Poseidon hashing for commitments/nullifiers, a fixed Merkle tree
 * for membership proofs, and snarkjs Groth16 for zero-knowledge proofs.
 */

import { Buffer } from 'buffer';
import { poseidon } from 'poseidon-hash';
import { MerkleTree } from 'fixed-merkle-tree';
import { buildBn128, utils as ffUtils } from 'ffjavascript';
import snarkjsLib from './snarkjs.bundled.js';

import { loadDefaultCircuitPaths } from './circuits/loadDefaultCircuitPaths.js';
import { resolveVerifiedCircuitArtifactPaths } from './circuits/verifyCircuitArtifacts.js';

const MERKLE_TREE_HEIGHT = 20;
const ZERO_ELEMENT = BigInt(
  '0x25b380d8dad7cb1672d367fac20393aa6e343c266e4b721c5db5fb8dbc0940f9'
).toString();

// ── Random Field Element ─────────────────────────────────────────────
// 31 bytes (248 bits) to stay within the BN254 snark field

export function generateRandomFieldElement() {
  const bytes = new Uint8Array(31);
  crypto.getRandomValues(bytes);
  return BigInt(
    '0x' + Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
  );
}

// ── Poseidon-based Commitment & Nullifier Hash ───────────────────────

export function createCommitment(nullifier, secret) {
  return poseidon([BigInt(nullifier), BigInt(secret)]);
}

export function createNullifierHash(nullifier) {
  return poseidon([BigInt(nullifier)]);
}

// ── Deposit Params ───────────────────────────────────────────────────
// Generates all the cryptographic material needed for a deposit and
// encodes it into a single "secret note" string the user must save.

export function generateDepositParams(amountLamports, mint = null) {
  const nullifier = generateRandomFieldElement();
  const secret = generateRandomFieldElement();
  const commitment = createCommitment(nullifier, secret);
  const nullifierHash = createNullifierHash(nullifier);

  const params = {
    nullifier: nullifier.toString(),
    secret: secret.toString(),
    amount: amountLamports.toString(),
    commitment: commitment.toString(),
    nullifierHash: nullifierHash.toString(),
    mint: mint || null,
  };

  // Compact note format (hex, no 0x): amount-nullifier-secret[-mint]
  // commitment/nullifierHash are recomputed from nullifier/secret when parsing.
  const noteParts = [
    BigInt(params.amount).toString(16),
    BigInt(params.nullifier).toString(16),
    BigInt(params.secret).toString(16),
  ];
  if (mint) {
    noteParts.push(mint);
  }
  const note = noteParts.join('-');

  return { params, note };
}

// ── Parse a secret note back into deposit params ─────────────────────

export function parseSecretNote(note) {
  const parts = String(note || '').trim().split('-');

  // Backward compatibility: old decimal format
  if (parts.length === 5) {
    const [amount, nullifier, secret, commitment, nullifierHash] = parts;
    return { amount, nullifier, secret, commitment, nullifierHash, mint: null };
  }

  // Token note: amountHex-nullifierHex-secretHex-mintPubkey
  if (parts.length === 4) {
    const [amountHex, nullifierHex, secretHex, mint] = parts;
    const amount = BigInt('0x' + amountHex).toString();
    const nullifier = BigInt('0x' + nullifierHex).toString();
    const secret = BigInt('0x' + secretHex).toString();
    const commitment = createCommitment(nullifier, secret).toString();
    const nullifierHash = createNullifierHash(nullifier).toString();
    return { amount, nullifier, secret, commitment, nullifierHash, mint };
  }

  // SOL / legacy compact format: amountHex-nullifierHex-secretHex
  if (parts.length !== 3) {
    throw new Error('Invalid note format');
  }

  const [amountHex, nullifierHex, secretHex] = parts;
  const amount = BigInt('0x' + amountHex).toString();
  const nullifier = BigInt('0x' + nullifierHex).toString();
  const secret = BigInt('0x' + secretHex).toString();
  const commitment = createCommitment(nullifier, secret).toString();
  const nullifierHash = createNullifierHash(nullifier).toString();

  return { amount, nullifier, secret, commitment, nullifierHash, mint: null };
}

// ── Public Key → Field Element ───────────────────────────────────────
// Takes the first 31 bytes of a Solana public key to fit the snark field.

export function publicKeyToFieldElement(publicKeyBytes) {
  // const slice = publicKeyBytes.slice(0, 31);
  // return BigInt(
  //   '0x' + Array.from(slice, (b) => b.toString(16).padStart(2, '0')).join('')
  // );
  const bytes = publicKeyBytes.toBytes().slice(0, 31);
  return BigInt("0x" + Buffer.from(bytes).toString("hex"));
}

// ── Amount → Field Element ───────────────────────────────────────────

export function amountToFieldElement(amount) {
  return BigInt(amount.toString());
}

// ── Merkle Proof ─────────────────────────────────────────────────────

export function generateMerkleProof(depositParams, leaves) {
  const tree = new MerkleTree(MERKLE_TREE_HEIGHT, leaves, {
    hashFunction: (a, b) => poseidon([BigInt(a), BigInt(b)]).toString(),
    zeroElement: ZERO_ELEMENT,
  });

  const leafIndex = leaves.findIndex((l) => l === depositParams.commitment);
  if (leafIndex === -1) {
    throw new Error('The deposit is not found in the tree');
  }

  const { pathElements, pathIndices } = tree.path(leafIndex);
  return { pathElements, pathIndices, root: tree.root };
}

// ── Full ZK Proof Generation (Groth16) ───────────────────────────────
// Uses bundled snarkjs (src/snarkjs.min.js) plus circuit WASM and zkey files.

export async function generateWithdrawProof(
  depositParams,
  leaves,
  recipientBytes,
  relayerBytes,
  fee,
  refund,
  snarkjs = snarkjsLib,
  circuitWasmPath,
  circuitZkeyPath
) {
  const defaults = await loadDefaultCircuitPaths();
  const wasmPath = circuitWasmPath ?? defaults.wasm;
  const zkeyPath = circuitZkeyPath ?? defaults.zkey;
  const verifiedCircuitPaths = await resolveVerifiedCircuitArtifactPaths(
    wasmPath,
    zkeyPath
  );
  const { root, pathElements, pathIndices } = generateMerkleProof(
    depositParams,
    leaves
  );
  const recipientField = publicKeyToFieldElement(recipientBytes);
  const relayerField = publicKeyToFieldElement(relayerBytes);
  const feeField = amountToFieldElement(fee);
  const refundField = amountToFieldElement(refund);

  const input = {
    root: BigInt(root),
    nullifierHash: BigInt(depositParams.nullifierHash || 0),
    recipient: recipientField,
    relayer: relayerField,
    fee: feeField,
    refund: refundField,
    nullifier: depositParams.nullifier,
    secret: depositParams.secret,
    pathElements,
    pathIndices,
  };
  let proof;
  let publicSignals;
  try {
    const proveResult = await snarkjs.groth16.fullProve(
      input,
      verifiedCircuitPaths.wasmPath,
      verifiedCircuitPaths.zkeyPath
    );
    proof = proveResult.proof;
    publicSignals = proveResult.publicSignals;
  } finally {
    if (verifiedCircuitPaths.cleanup) {
      verifiedCircuitPaths.cleanup();
    }
  }

  return {
    proof,
    publicSignals,
    args: [
      toFixedHex(input.root),
      toFixedHex(input.nullifierHash),
      input.recipient,
      input.relayer,
      toFixedHex(input.fee),
      toFixedHex(input.refund),
    ],
  };
}

// ── Convert Groth16 Proof to 256-byte On-chain Format ────────────────

function g1Uncompressed(curve, p1Raw) {
  const p1 = curve.G1.fromObject(p1Raw);
  const buff = new Uint8Array(64);
  curve.G1.toRprUncompressed(buff, 0, p1);
  return buff;
}

function g2Uncompressed(curve, p2Raw) {
  const p2 = curve.G2.fromObject(p2Raw);
  const buff = new Uint8Array(128);
  curve.G2.toRprUncompressed(buff, 0, p2);
  return buff;
}

export async function convertProofToBytes(proof) {
  const { unstringifyBigInts } = ffUtils;
  const proofProc = unstringifyBigInts(proof);

  const curve = await buildBn128();
  const pi_a = g1Uncompressed(curve, proofProc.pi_a);
  const pi_b = g2Uncompressed(curve, proofProc.pi_b);
  const pi_c = g1Uncompressed(curve, proofProc.pi_c);

  const allBytes = new Uint8Array(256);
  allBytes.set(pi_a, 0);
  allBytes.set(pi_b, 64);
  allBytes.set(pi_c, 192);

  if (allBytes.length !== 256) {
    throw new Error(`Expected 256 bytes, but got ${allBytes.length}`);
  }

  return Array.from(allBytes);
}

// ── 32-byte Buffer from BigInt ───────────────────────────────────────

export function to32ByteBuffer(bigInt) {
  const hex = bigInt.toString(16).padStart(64, '0');
  const bytes = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytes;
}

// ── Hex Helpers ──────────────────────────────────────────────────────

export function toFixedHex(value, length = 32) {
  const hex = BigInt(value.toString()).toString(16);
  return '0x' + hex.padStart(length * 2, '0');
}

/** Normalize commitment/nullifier values to 64-char hex for indexer API calls. */
export function toFieldElementHex(value) {
  if (value == null || value === '') return '';
  const str = String(value).trim();
  if (/^0x[0-9a-fA-F]+$/i.test(str)) {
    return str.slice(2).padStart(64, '0').toLowerCase();
  }
  if (/^[0-9a-fA-F]{64}$/i.test(str)) {
    return str.toLowerCase();
  }
  return BigInt(str).toString(16).padStart(64, '0');
}

// ── Format a note for display (truncated) ────────────────────────────

export function formatNoteForDisplay(note, segmentLen = 8) {
  const parts = note.split('-');
  return parts
    .map((p) =>
      p.length > segmentLen * 2
        ? p.slice(0, segmentLen) + '…' + p.slice(-segmentLen)
        : p
    )
    .join('-');
}
